import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { ShieldWarning, ArrowsClockwise, MagnifyingGlass, Bug, Ticket as TicketIcon, CheckCircle, Package as PackageIcon, Download, Bell, BellSlash, Gear, FileText, TrendUp } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer } from "recharts";

const SEVERITY_CLS = {
  CRITICAL: "bg-red-100 text-red-800 border-red-400",
  HIGH: "bg-orange-100 text-orange-800 border-orange-400",
  MEDIUM: "bg-amber-100 text-amber-800 border-amber-400",
  LOW: "bg-yellow-50 text-yellow-800 border-yellow-300",
  UNKNOWN: "bg-gray-100 text-gray-700 border-gray-300",
};
const STATUS_LABEL = {
  open: "Open",
  acknowledged: "Acknowledged",
  resolved: "Resolved",
  wontfix: "Won't fix",
  accepted: "Accepted risk",
  resolved_upstream: "Already patched upstream",
  dev_only: "Dev-only (build)",
  environmental: "Environmental (base image)",
  platform_pinned: "Platform-pinned (Emergent-managed)",
};
const STATUS_CLS = {
  open: "bg-red-50 text-red-700 border-red-300",
  acknowledged: "bg-amber-50 text-amber-700 border-amber-300",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-300",
  wontfix: "bg-gray-100 text-gray-600 border-gray-300",
  accepted: "bg-violet-50 text-violet-700 border-violet-300",
  resolved_upstream: "bg-emerald-50 text-emerald-700 border-emerald-300",
  dev_only: "bg-slate-100 text-slate-600 border-slate-300",
  environmental: "bg-sky-50 text-sky-700 border-sky-300",
  platform_pinned: "bg-violet-50 text-violet-700 border-violet-300",
};
const ECOSYSTEM_LABEL = {
  PyPI: "Python (PyPI)",
  npm: "JavaScript (npm)",
  Debian: "Debian / OS",
  runtime: "Runtime",
};

function StatCard({ label, value, tone = "default", "data-testid": tid }) {
  const toneCls = {
    default: "border-border bg-white",
    critical: "border-red-400 bg-red-50",
    high: "border-orange-400 bg-orange-50",
    medium: "border-amber-400 bg-amber-50",
    low: "border-yellow-300 bg-yellow-50",
    ok: "border-emerald-400 bg-emerald-50",
  }[tone];
  return (
    <div data-testid={tid} className={`border ${toneCls} px-4 py-3 rounded-none`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className="text-2xl font-display font-bold text-[#071B3E] mt-0.5">{value}</div>
    </div>
  );
}

export default function SecurityPosture() {
  const [overview, setOverview] = useState(null);
  const [findings, setFindings] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [q, setQ] = useState("");
  const [sevFilter, setSevFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open,acknowledged");
  const [ecoFilter, setEcoFilter] = useState("all");
  const [showSuppressed, setShowSuppressed] = useState(false);
  const [editing, setEditing] = useState(null); // finding being edited
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [memoExporting, setMemoExporting] = useState(false);
  const [trend, setTrend] = useState(null);

  const loadOverview = useCallback(async () => {
    try { const r = await api.get("/admin/security/overview"); setOverview(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load overview"); }
  }, []);

  const loadFindings = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (sevFilter !== "all") params.set("severity", sevFilter);
      // Suppressed-type statuses are auto_suppressed in the DB, so viewing them
      // requires show_suppressed regardless of the checkbox.
      const needsSuppressed = /resolved_upstream|dev_only|environmental|platform_pinned/.test(statusFilter || "");
      if (showSuppressed || needsSuppressed) params.set("show_suppressed", "true");
      const r = await api.get(`/admin/security/findings?${params}`);
      setFindings(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load findings"); }
  }, [statusFilter, sevFilter, showSuppressed]);

  const loadInventory = useCallback(async () => {
    try { const r = await api.get("/admin/security/inventory"); setInventory(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load inventory"); }
  }, []);

  const loadSettings = useCallback(async () => {
    try { const r = await api.get("/admin/security/settings"); setSettings(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load settings"); }
  }, []);

  const loadTrend = useCallback(async () => {
    try { const r = await api.get("/admin/security/trend?days=30"); setTrend(r.data?.points || []); }
    catch (e) { setTrend([]); }
  }, []);

  useEffect(() => { loadOverview(); loadFindings(); loadInventory(); loadSettings(); loadTrend(); }, [loadOverview, loadFindings, loadInventory, loadSettings, loadTrend]);

  const saveSettings = async (patch) => {
    try {
      const r = await api.put("/admin/security/settings", patch);
      setSettings(r.data);
      toast.success("Setting updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (sevFilter !== "all") params.set("severity", sevFilter);
      const needsSuppressed = /resolved_upstream|dev_only|environmental|platform_pinned/.test(statusFilter || "");
      if (showSuppressed || needsSuppressed) params.set("show_suppressed", "true");
      const res = await api.get(`/admin/security/export.csv?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `claroshield_cve_findings_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
    finally { setExporting(false); }
  };

  const exportMemo = async () => {
    setMemoExporting(true);
    try {
      const res = await api.get("/admin/security/applicability-memo.docx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ClaroShield_Vulnerability_Applicability_Memo_${new Date().toISOString().slice(0, 10)}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Applicability memo downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Memo export failed"); }
    finally { setMemoExporting(false); }
  };

  // Poll overview while a scan is running
  useEffect(() => {
    if (!overview?.running) return;
    const t = setInterval(() => { loadOverview(); }, 4000);
    return () => clearInterval(t);
  }, [overview?.running, loadOverview]);

  // Reload findings/inventory after a scan finishes
  const [wasRunning, setWasRunning] = useState(false);
  useEffect(() => {
    if (overview?.running) { setWasRunning(true); return; }
    if (wasRunning && !overview?.running) {
      setWasRunning(false);
      loadFindings();
      loadInventory();
      loadTrend();
    }
  }, [overview?.running, wasRunning, loadFindings, loadInventory, loadTrend]);

  const startScan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/admin/security/scan");
      if (r.data.status === "already_running") {
        toast.message("A scan is already running.");
      } else {
        toast.success("Scan started — this typically takes 15-60 seconds.");
      }
      loadOverview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Scan failed to start"); }
    finally { setScanning(false); }
  };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (findings || []).filter((f) => {
      if (ecoFilter !== "all" && f.ecosystem !== ecoFilter) return false;
      if (!ql) return true;
      return `${f.cve_id} ${f.component} ${f.version} ${f.summary}`.toLowerCase().includes(ql);
    });
  }, [findings, q, ecoFilter]);

  const updateFinding = async (patch) => {
    if (!editing) return;
    try {
      const r = await api.patch(`/admin/security/findings/${editing.id}`, patch);
      setFindings((cur) => (cur || []).map((f) => f.id === editing.id ? { ...f, ...r.data } : f));
      toast.success("Finding updated");
      setEditing(null);
      loadOverview();
      loadFindings();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const createTicket = async (f) => {
    try {
      const r = await api.post(`/admin/security/findings/${f.id}/create-ticket`, {});
      setFindings((cur) => (cur || []).map((x) => x.id === f.id ? { ...x, ticket_id: r.data.ticket_id } : x));
      toast.success(r.data.already_existed ? "Ticket already exists — linked." : "Ticket created in ClaroFlow.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Ticket creation failed"); }
  };

  const oc = overview?.open_counts || {};
  const last = overview?.last_scan;

  return (
    <div data-testid="security-posture" className="space-y-4">
      {/* Overview */}
      <div className="border border-border bg-white p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-bold text-[#071B3E] flex items-center gap-2">
              <ShieldWarning size={20} weight="fill" className="text-indigo-700" />
              Software Vulnerabilities (OSV.dev)
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Continuous inventory of every runtime, Python package, npm package, and key OS binary running the app.
              Cross-checked against Google&apos;s OSV.dev vulnerability database on backend boot and on demand.
              HIGH/CRITICAL findings auto-create a ClaroFlow remediation ticket; Super Admins are emailed about new findings that aren&apos;t accepted (at or above the email severity floor).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="sec-export-csv" onClick={exportCsv} disabled={exporting}
              variant="outline" size="sm" className="rounded-none">
              <Download size={14} weight="bold" className="mr-1" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            <Button data-testid="sec-export-memo" onClick={exportMemo} disabled={memoExporting}
              variant="outline" size="sm" className="rounded-none"
              title="Branded Word memo classifying every non-app-fixable finding (Not Applicable / Backported / Base-image pending) — ready for an audit packet">
              <FileText size={14} weight="bold" className="mr-1" />
              {memoExporting ? "Building…" : "Applicability Memo"}
            </Button>
            <Button data-testid="sec-settings-btn" onClick={() => setSettingsOpen(true)}
              variant="outline" size="sm" className="rounded-none">
              <Gear size={14} weight="bold" className="mr-1" />Settings
              {settings && !settings.email_admins_enabled && (
                <BellSlash size={12} weight="fill" className="ml-1 text-red-500" title="Admin emails are OFF" />
              )}
            </Button>
            <Button data-testid="sec-scan-now" onClick={startScan} disabled={scanning || overview?.running}
              className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]" size="sm">
              <ArrowsClockwise size={14} weight="bold" className={`mr-1 ${overview?.running ? "animate-spin" : ""}`} />
              {overview?.running ? "Scanning…" : "Scan now"}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mt-3">
          <StatCard label="Critical (actionable)" value={oc.CRITICAL || 0} tone={oc.CRITICAL ? "critical" : "ok"} data-testid="stat-critical" />
          <StatCard label="High (actionable)" value={oc.HIGH || 0} tone={oc.HIGH ? "high" : "ok"} data-testid="stat-high" />
          <StatCard label="Medium" value={oc.MEDIUM || 0} tone={oc.MEDIUM ? "medium" : "default"} data-testid="stat-medium" />
          <StatCard label="Low / Unknown" value={(oc.LOW || 0) + (oc.UNKNOWN || 0)} tone="default" data-testid="stat-low" />
          <StatCard label="Inventory items" value={overview?.inventory_count || 0} data-testid="stat-inventory" />
          <StatCard label="Total actionable / raw" value={`${overview?.total_actionable || 0} / ${overview?.total_findings || 0}`} data-testid="stat-total" />
        </div>
        {Array.isArray(trend) && trend.length >= 2 && (
          <div data-testid="sec-trend" className="mt-3 border border-border bg-white px-3 py-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5">
                <TrendUp size={13} weight="bold" className="text-indigo-700" />
                Actionable vulnerabilities — 30-day trend
              </span>
              {(() => {
                const first = trend[0], last = trend[trend.length - 1];
                const delta = (last.actionable || 0) - (first.actionable || 0);
                const cls = delta < 0 ? "text-emerald-700" : delta > 0 ? "text-red-700" : "text-muted-foreground";
                const label = delta < 0 ? `▼ ${Math.abs(delta)} fewer` : delta > 0 ? `▲ ${delta} more` : "no change";
                return <span className={`text-[11px] font-semibold ${cls}`} data-testid="sec-trend-delta">{label} vs {trend.length} days ago</span>;
              })()}
            </div>
            <div style={{ width: "100%", height: 90 }}>
              <ResponsiveContainer>
                <AreaChart data={trend} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="critGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#dc2626" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#dc2626" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="highGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ea580c" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#ea580c" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={(d) => (d || "").slice(5)} minTickGap={24} />
                  <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} allowDecimals={false} width={30} />
                  <RTooltip contentStyle={{ fontSize: 11, borderRadius: 0 }}
                    labelFormatter={(d) => `Date: ${d}`}
                    formatter={(v, n) => [v, n === "critical" ? "Critical" : n === "high" ? "High" : n]} />
                  <Area type="monotone" dataKey="high" stroke="#ea580c" strokeWidth={1.5} fill="url(#highGrad)" />
                  <Area type="monotone" dataKey="critical" stroke="#dc2626" strokeWidth={2} fill="url(#critGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {overview?.suppressed && (
          <div data-testid="sec-suppressed-banner" className="mt-3 border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-[11px] text-[#071B3E] flex flex-wrap items-center gap-3">
            <span className="font-bold uppercase tracking-wider text-indigo-700">Auto-suppressed noise</span>
            <span><b>{overview.suppressed.already_patched}</b> already patched upstream (installed version has the fix)</span>
            <span className="text-indigo-300">|</span>
            <span><b>{overview.suppressed.environmental}</b> environmental Debian OS (patched via base-image redeploy)</span>
            <span className="text-indigo-300">|</span>
            <span><b>{overview.suppressed.dev_only}</b> dev-only npm build tools (not shipped)</span>
            {overview.suppressed.platform_pinned > 0 && (
              <>
                <span className="text-indigo-300">|</span>
                <span><b>{overview.suppressed.platform_pinned}</b> platform-pinned (Emergent-managed dependency — risk-accepted, unfixable in-app)</span>
              </>
            )}
            <span className="text-muted-foreground">— toggle &ldquo;Include auto-suppressed&rdquo; in Findings to see them.</span>
          </div>
        )}
        {last && (
          <p className="text-[11px] text-muted-foreground mt-2" data-testid="sec-last-scan">
            Last scan: {new Date(last.started_at).toLocaleString()} · trigger: {last.trigger} · new: {last.new_findings} · by: {last.by}
            <span className="block text-[11px]" data-testid="sec-monthly-schedule">Automatic monthly scan: 1st of each month, 06:00 UTC — Super Admins are emailed about new findings that aren&apos;t accepted.</span>
          </p>
        )}
      </div>

      <Tabs defaultValue="findings">
        <TabsList className="rounded-none">
          <TabsTrigger value="findings" data-testid="sec-tab-findings" className="rounded-none">
            <Bug size={14} weight="fill" className="mr-1" />Findings
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="sec-tab-inventory" className="rounded-none">
            <PackageIcon size={14} weight="fill" className="mr-1" />Inventory ({overview?.inventory_count || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="findings">
          <div className="border border-border bg-white p-3 mt-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative min-w-[240px]">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="sec-findings-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search CVE, component, summary…" className="rounded-none pl-8 h-8" />
              </div>
              <Select value={sevFilter} onValueChange={setSevFilter}>
                <SelectTrigger data-testid="sec-sev-filter" className="rounded-none h-8 w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All severities</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="UNKNOWN">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="sec-status-filter" className="rounded-none h-8 w-[220px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open,acknowledged">Open + Acknowledged</SelectItem>
                  <SelectItem value="open">Open only</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged only</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="wontfix">Won&apos;t fix</SelectItem>
                  <SelectItem value="accepted">Accepted risk</SelectItem>
                  <SelectItem value="resolved_upstream">Already patched upstream</SelectItem>
                  <SelectItem value="dev_only">Dev-only (build)</SelectItem>
                  <SelectItem value="environmental">Environmental (base image)</SelectItem>
                  <SelectItem value="platform_pinned">Platform-pinned (Emergent-managed)</SelectItem>
                  <SelectItem value="open,acknowledged,resolved,wontfix,accepted,resolved_upstream,dev_only,environmental,platform_pinned">All statuses</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ecoFilter} onValueChange={setEcoFilter}>
                <SelectTrigger data-testid="sec-eco-filter" className="rounded-none h-8 w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ecosystems</SelectItem>
                  <SelectItem value="PyPI">Python (PyPI)</SelectItem>
                  <SelectItem value="npm">JavaScript (npm)</SelectItem>
                  <SelectItem value="Debian">Debian / OS</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground cursor-pointer">
                <input data-testid="sec-show-suppressed" type="checkbox" checked={showSuppressed}
                  onChange={(e) => setShowSuppressed(e.target.checked)} className="accent-indigo-600" />
                Include auto-suppressed
              </label>
              <div className="ml-auto text-[11px] text-muted-foreground">{filtered.length} finding(s)</div>
            </div>

            {findings === null ? (
              <div className="text-sm text-muted-foreground py-6 text-center">Loading findings…</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center" data-testid="sec-findings-empty">
                {(findings || []).length === 0 ? "No findings yet. Run a scan to check the software inventory against OSV.dev." : "No findings match the current filters."}
              </div>
            ) : (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b-2 border-border">
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Severity</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">CVE</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Component</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Current version</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Identified</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Fix</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Status</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Ticket</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((f) => (
                      <tr key={f.id} data-testid={`sec-finding-${f.id}`} className="border-b border-border align-top hover:bg-indigo-50/30">
                        <td className="py-2 pr-2">
                          <span className={`text-[10px] uppercase font-bold tracking-wider border px-1.5 py-0.5 ${SEVERITY_CLS[f.severity]}`}>{f.severity}</span>
                        </td>
                        <td className="py-2 pr-2 font-mono text-xs text-[#071B3E]" title={f.summary}>
                          <div>{f.cve_id}</div>
                          {f.summary && <div className="text-[11px] text-muted-foreground font-sans mt-0.5 max-w-md line-clamp-2">{f.summary}</div>}
                        </td>
                        <td className="py-2 pr-2">
                          <div className="text-xs font-semibold text-[#071B3E]">{f.component}</div>
                          <div className="text-[10px] text-muted-foreground uppercase">{ECOSYSTEM_LABEL[f.ecosystem] || f.ecosystem}</div>
                        </td>
                        <td className="py-2 pr-2 font-mono text-xs">{f.version}</td>
                        <td className="py-2 pr-2 text-xs">
                          {f.published ? (
                            <>
                              <div className="font-mono text-[11px]">{f.published.slice(0, 10)}</div>
                              {typeof f.days_known === "number" && (
                                <div className={`text-[10px] ${f.days_known > 365 ? "text-red-600 font-semibold" : f.days_known > 90 ? "text-amber-600" : "text-muted-foreground"}`}>
                                  {f.days_known} day{f.days_known === 1 ? "" : "s"} known
                                </div>
                              )}
                            </>
                          ) : <span className="text-muted-foreground italic">—</span>}
                        </td>
                        <td className="py-2 pr-2 text-xs">
                          {f.fix_state === "already_applied" ? (
                            <>
                              <div className="text-[11px] font-semibold text-emerald-700">Applied</div>
                              <div className="text-[10px] text-muted-foreground">in v{f.version}</div>
                            </>
                          ) : f.fix_state === "available" ? (
                            <>
                              <div className="text-[11px] font-semibold text-orange-700">Upgrade to</div>
                              <div className="font-mono text-[11px] text-emerald-700">{(f.fixed_versions || []).join(", ")}</div>
                            </>
                          ) : (
                            <>
                              <div className="text-[11px] font-semibold text-red-600">No fix released</div>
                              <div className="text-[10px] text-muted-foreground">
                                {f.ecosystem === "Debian" ? "Awaits base-image update" : "Awaits vendor release"}
                              </div>
                            </>
                          )}
                        </td>
                        <td className="py-2 pr-2">
                          {(() => {
                            // Effective status: derived from the actual state, not just the raw
                            // status field. If the fix is applied / environment-owned / dev-only,
                            // showing "Open" would contradict the Fix column — so surface the
                            // real state and keep the raw admin status (acknowledged/resolved/wontfix)
                            // as an override.
                            const raw = f.status || "open";
                            if (["acknowledged", "resolved", "wontfix", "accepted"].includes(raw)) {
                              return (
                                <span className={`text-[10px] uppercase font-bold tracking-wide border px-1.5 py-0.5 ${STATUS_CLS[raw] || STATUS_CLS.open}`}>
                                  {STATUS_LABEL[raw]}
                                </span>
                              );
                            }
                            if (f.platform_pinned) {
                              return (
                                <span className="text-[10px] uppercase font-bold tracking-wide border px-1.5 py-0.5 bg-violet-50 text-violet-700 border-violet-300" title={f.mitigation_note || `Version controlled by ${f.pinned_by || "an Emergent-managed library"} — cannot be upgraded in-app; documented risk acceptance`}>
                                  Platform-pinned
                                </span>
                              );
                            }
                            if (f.already_patched) {
                              return (
                                <span className="text-[10px] uppercase font-bold tracking-wide border px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-300" title="Installed version already contains the CVE fix — visible for audit trail because severity is HIGH+">
                                  Patched
                                </span>
                              );
                            }
                            if (f.environmental) {
                              return (
                                <span className="text-[10px] uppercase font-bold tracking-wide border px-1.5 py-0.5 bg-sky-50 text-sky-700 border-sky-300" title="Debian OS package with no upstream fix yet — remediated when base container image is rebuilt">
                                  Environmental
                                </span>
                              );
                            }
                            if (f.dev_only) {
                              return (
                                <span className="text-[10px] uppercase font-bold tracking-wide border px-1.5 py-0.5 bg-slate-100 text-slate-600 border-slate-300" title="Build-time dev dependency, not shipped to end users">
                                  Dev-only
                                </span>
                              );
                            }
                            return (
                              <span className="text-[10px] uppercase font-bold tracking-wide border px-1.5 py-0.5 bg-red-50 text-red-700 border-red-300">
                                Open
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-2 pr-2">
                          {f.ticket_id ? (
                            (f.already_patched || f.ticket_status === "closed") ? (
                              <span className="text-[11px] inline-flex items-center gap-1 text-emerald-700 font-semibold" title="ClaroFlow ticket auto-closed — fix has been applied">
                                <CheckCircle size={11} weight="fill" />Closed
                              </span>
                            ) : (
                              <span className="text-[11px] inline-flex items-center gap-1 text-orange-700 font-semibold" title="ClaroFlow ticket open — awaiting remediation">
                                <TicketIcon size={11} weight="fill" />Open
                              </span>
                            )
                          ) : (
                            <button data-testid={`sec-create-ticket-${f.id}`} onClick={() => createTicket(f)}
                              className="text-[11px] inline-flex items-center gap-1 font-semibold text-indigo-700 border border-indigo-300 px-1.5 py-0.5 hover:bg-indigo-50">
                              <TicketIcon size={11} />Create ticket
                            </button>
                          )}
                        </td>
                        <td className="py-2 pr-2 text-right">
                          <button data-testid={`sec-open-${f.id}`} onClick={() => setEditing(f)}
                            className="text-[11px] font-semibold text-indigo-700 hover:underline">Manage</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="inventory">
          <div className="border border-border bg-white p-3 mt-3">
            <p className="text-xs text-muted-foreground mb-2">
              Snapshot from last scan{inventory?.captured_at ? ` — ${new Date(inventory.captured_at).toLocaleString()}` : ""}.
              Runtime binaries and non-scannable ecosystems are captured for reference but not queried against OSV.dev.
            </p>
            {inventory === null ? (
              <div className="text-sm text-muted-foreground py-6 text-center">Loading inventory…</div>
            ) : (inventory?.items || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">No inventory yet — run a scan.</div>
            ) : (
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left border-b-2 border-border">
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Component</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Version</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Ecosystem</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">Source</th>
                      <th className="py-2 pr-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">CVE scanned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inventory.items || []).map((it, i) => (
                      <tr key={`${it.ecosystem}-${it.name}-${it.version}-${i}`} className="border-b border-border hover:bg-indigo-50/30">
                        <td className="py-1.5 pr-2 text-xs font-semibold text-[#071B3E]">{it.name}</td>
                        <td className="py-1.5 pr-2 font-mono text-xs">{it.version}</td>
                        <td className="py-1.5 pr-2 text-[11px]">{ECOSYSTEM_LABEL[it.ecosystem] || it.ecosystem}</td>
                        <td className="py-1.5 pr-2 text-[11px] text-muted-foreground">{it.source}</td>
                        <td className="py-1.5 pr-2 text-[11px]">
                          {it.scan_enabled ? <span className="text-emerald-700 font-semibold">Yes</span> : <span className="text-muted-foreground">No</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Finding manage dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug size={18} weight="fill" className="text-indigo-700" />
              {editing?.cve_id}
              {editing && <span className={`text-[10px] uppercase font-bold tracking-wider border px-1.5 py-0.5 ${SEVERITY_CLS[editing.severity]}`}>{editing.severity}</span>}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Component</div>
                <div className="text-[#071B3E] font-semibold">{editing.component} {editing.version} <span className="text-[11px] font-normal text-muted-foreground">({ECOSYSTEM_LABEL[editing.ecosystem] || editing.ecosystem})</span></div>
              </div>
              {editing.summary && (
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Summary</div>
                  <div className="text-sm">{editing.summary}</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Identified (disclosed)</div>
                  <div className="text-xs font-mono">{(editing.published || "").slice(0, 10) || "—"}</div>
                  {typeof editing.days_known === "number" && (
                    <div className="text-[10px] text-muted-foreground">{editing.days_known} days known</div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Data last updated</div>
                  <div className="text-xs font-mono">{(editing.modified || "").slice(0, 10) || "—"}</div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Fix status</div>
                {editing.fix_state === "already_applied" ? (
                  <div className="text-sm text-emerald-700 font-semibold">Fix already applied — installed version <span className="font-mono">{editing.version}</span> incorporates the remediation shipped in {(editing.fixed_versions || []).join(", ")}.</div>
                ) : editing.platform_pinned ? (
                  <div className="text-sm text-violet-700 font-semibold">Fix exists upstream (<span className="font-mono">{(editing.fixed_versions || []).join(", ") || "later release"}</span>) but cannot be applied — version is controlled by {editing.pinned_by || "an Emergent-managed library"}. See risk acceptance below.</div>
                ) : editing.fix_state === "available" ? (
                  <div className="text-sm text-orange-700 font-semibold">Fix available — upgrade to <span className="font-mono">{(editing.fixed_versions || []).join(", ")}</span>.</div>
                ) : (
                  <div className="text-sm text-red-600 font-semibold">
                    No fix released yet — {editing.ecosystem === "Debian" ? "awaits Debian security update / base-image rebuild." : "awaits vendor release."}
                  </div>
                )}
              </div>
              {editing.platform_pinned && (
                <div data-testid="sec-edit-platform-pinned" className="border border-violet-300 bg-violet-50 p-3">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-violet-700 flex items-center gap-1.5">
                    <ShieldWarning size={13} weight="fill" />Platform-pinned — documented risk acceptance
                  </div>
                  <div className="text-xs text-[#071B3E] mt-1 whitespace-pre-line">{editing.mitigation_note}</div>
                  {editing.pinned_by && (
                    <div className="text-[10px] text-violet-700/80 mt-1.5 font-semibold">Version controlled by: {editing.pinned_by}</div>
                  )}
                </div>
              )}
              {editing.details && (
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Details</div>
                  <div className="text-xs text-foreground/80 max-h-40 overflow-y-auto whitespace-pre-line border border-border p-2 bg-secondary/30">{editing.details}</div>
                </div>
              )}
              {(editing.references || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">References</div>
                  <ul className="text-xs list-disc pl-4 space-y-0.5">
                    {editing.references.slice(0, 6).map((r) => (
                      <li key={r}><a href={r} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline break-all">{r}</a></li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                {editing.status === "accepted" && editing.accepted_by && (
                  <div className="text-[11px] text-violet-700 mb-1" data-testid="sec-edit-accepted-by">
                    Risk accepted by {editing.accepted_by} on {new Date(editing.accepted_at).toLocaleString()}
                  </div>
                )}
                <div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Notes (admin)</div>
                <Textarea data-testid="sec-edit-notes" value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  placeholder="Add remediation notes, compensating controls, or context…"
                  className="rounded-none mt-1 min-h-[70px] text-sm" />
              </div>
              <div className="flex items-center gap-2 border-t border-border pt-3">
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="sec-edit-status" className="rounded-none h-8 w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="wontfix">Won&apos;t fix</SelectItem>
                    <SelectItem value="accepted" data-testid="sec-edit-status-accepted">Accepted risk (reason required)</SelectItem>
                  </SelectContent>
                </Select>
                {!editing.ticket_id && (
                  <Button data-testid="sec-edit-create-ticket" onClick={() => createTicket(editing)}
                    size="sm" variant="outline" className="rounded-none border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    <TicketIcon size={13} className="mr-1" />Create ticket
                  </Button>
                )}
                {editing.ticket_id && (editing.already_patched || editing.ticket_status === "closed") && (
                  <span className="text-[11px] inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <CheckCircle size={12} weight="fill" />Ticket closed (auto — fix applied)
                  </span>
                )}
                {editing.ticket_id && !(editing.already_patched || editing.ticket_status === "closed") && (
                  <span className="text-[11px] inline-flex items-center gap-1 text-orange-700 font-semibold">
                    <TicketIcon size={12} weight="fill" />Ticket open — awaiting remediation
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Button data-testid="sec-edit-cancel" onClick={() => setEditing(null)} variant="outline" size="sm" className="rounded-none">Cancel</Button>
                  <Button data-testid="sec-edit-save" onClick={() => {
                    if (editing.status === "accepted" && (editing.notes || "").trim().length < 10) {
                      toast.error("Enter the reason for accepting this risk in Notes (at least 10 characters)");
                      return;
                    }
                    updateFinding({ status: editing.status, notes: editing.notes });
                  }}
                    size="sm" className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]">Save</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scanner settings — email suppression, auto-ticket toggle, startup scan */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gear size={18} weight="fill" className="text-indigo-700" />
              Vulnerability scanner settings
            </DialogTitle>
          </DialogHeader>
          {settings && (
            <div className="space-y-4 text-sm">
              <div className="border border-border p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="sec-set-email-enabled" type="checkbox"
                    checked={settings.email_admins_enabled}
                    onChange={(e) => saveSettings({ email_admins_enabled: e.target.checked })}
                    className="mt-0.5 accent-indigo-600" />
                  <div>
                    <div className="font-semibold text-[#071B3E] flex items-center gap-1.5">
                      {settings.email_admins_enabled ? <Bell size={14} weight="fill" className="text-indigo-700" /> : <BellSlash size={14} weight="fill" className="text-red-500" />}
                      Email admins on new findings
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Sends a single digest email to every Super Admin when a scan surfaces new findings at or above the severity floor below. Turn OFF to silence all scanner emails.
                    </div>
                  </div>
                </label>
                {settings.email_admins_enabled && (
                  <div className="mt-2 pl-6">
                    <label className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground">Minimum severity for email</label>
                    <Select value={settings.email_admins_severity}
                      onValueChange={(v) => saveSettings({ email_admins_severity: v })}>
                      <SelectTrigger data-testid="sec-set-email-severity" className="rounded-none h-8 mt-1 w-[220px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CRITICAL">Critical only</SelectItem>
                        <SelectItem value="HIGH">High + Critical</SelectItem>
                        <SelectItem value="MEDIUM">Medium and above</SelectItem>
                        <SelectItem value="LOW">All new findings (Low and above)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="border border-border p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="sec-set-auto-ticket" type="checkbox"
                    checked={settings.auto_tickets_enabled}
                    onChange={(e) => saveSettings({ auto_tickets_enabled: e.target.checked })}
                    className="mt-0.5 accent-indigo-600" />
                  <div>
                    <div className="font-semibold text-[#071B3E]">Auto-create ClaroFlow ticket per finding</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      When a scan detects a NEW actionable HIGH or CRITICAL finding, a remediation ticket is opened in ClaroFlow (CRITICAL → 7-day SLA, HIGH → 30-day). You can still create tickets manually from any finding row.
                    </div>
                  </div>
                </label>
              </div>

              <div className="border border-border p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="sec-set-startup-scan" type="checkbox"
                    checked={settings.startup_scan_enabled}
                    onChange={(e) => saveSettings({ startup_scan_enabled: e.target.checked })}
                    className="mt-0.5 accent-indigo-600" />
                  <div>
                    <div className="font-semibold text-[#071B3E]">Run a scan on backend startup</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Kicks off one scan ~90 seconds after each backend boot, but only if the last successful scan is older than 12 hours. Prevents hot-reload thrashing.
                    </div>
                  </div>
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
                <Button data-testid="sec-settings-close" onClick={() => setSettingsOpen(false)}
                  className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]" size="sm">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
